import { Link, useRouterState } from "@tanstack/react-router";
import { BuildInfo } from "@cliqsuite/ui";

export interface NavItemDef {
  path: string;
  label: string;
  icon: string;
  sub: string;
}

export const NAV_ITEMS: NavItemDef[] = [
  { path: "/", label: "Verification", icon: "fact_check", sub: "Check each proof, then approve to activate the plan" },
  { path: "/records", label: "Billing records", icon: "receipt_long", sub: "Every approved payment and the invoice it created" },
  { path: "/plans", label: "Plans", icon: "workspace_premium", sub: "What businesses can buy, and how cycles are discounted" },
  { path: "/businesses", label: "Businesses", icon: "domain", sub: "Subscriptions across every CliqSuite business" },
  { path: "/discounts", label: "Discounts", icon: "sell", sub: "Price cuts for businesses and codes they type at checkout" },
  { path: "/methods", label: "Payment methods", icon: "account_balance_wallet", sub: "Where businesses pay CliqSuite. They see these when they renew." },
  { path: "/devices", label: "Devices", icon: "devices", sub: "Look up any register, kitchen screen or terminal by its pairing code" },
  { path: "/audit", label: "Audit log", icon: "history", sub: "Everything admins and the system changed. Entries can't be edited." },
];

export const PAGE_TITLES: Record<string, { title: string; sub: string }> = {
  "/": { title: "Payment verification", sub: NAV_ITEMS[0]!.sub },
  "/plans": { title: "Subscription plans", sub: NAV_ITEMS[2]!.sub },
  ...Object.fromEntries(NAV_ITEMS.filter((n) => n.path !== "/" && n.path !== "/plans").map((n) => [n.path, { title: n.label, sub: n.sub }])),
};

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Sidebar({
  pendingCount,
  adminName,
  onLogout,
  drawer = false,
  onNavigate,
}: {
  pendingCount: number;
  adminName: string;
  onLogout: () => void;
  drawer?: boolean;
  onNavigate?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <aside className={`o-side${drawer ? " o-side--drawer" : ""}`} style={{ width: 248, flexBasis: 248 }}>
      <div className="o-brand">
        <span className="o-brand__mark">C</span>
        <span>
          <span className="o-brand__name">CliqSuite</span>
          <span className="o-brand__role">PLATFORM ADMIN</span>
        </span>
      </div>
      <nav aria-label="Admin" className="o-nav">
        {NAV_ITEMS.map((n) => {
          const active = pathname === n.path;
          const badge = n.path === "/" ? pendingCount : 0;
          return (
            <Link key={n.path} to={n.path} onClick={onNavigate} className={`o-nav-item${active ? " is-active" : ""}`} aria-current={active ? "page" : undefined}>
              <span className="ms" aria-hidden="true">{n.icon}</span>
              <span style={{ flex: 1 }}>{n.label}</span>
              {badge > 0 && <span className="o-nav-item__count num">{badge}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="o-me">
        <span className="o-me__avatar">{initials(adminName)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="o-me__name">{adminName}</span>
          <span className="o-me__role" style={{ textTransform: "none" }}>Platform admin</span>
        </span>
        <button type="button" aria-label="Sign out" className="o-me__out" onClick={onLogout}>
          <span className="ms" aria-hidden="true">logout</span>
        </button>
      </div>
      <div style={{ marginTop: 10, padding: "0 4px" }}>
        <BuildInfo sha={__BUILD_SHA__} time={__BUILD_TIME__} color="rgba(255,255,255,.4)" />
      </div>
    </aside>
  );
}
